import React, { useState, useMemo } from 'react';
import { Table, Badge } from 'react-bootstrap';

export default function SearchTablePaginationView(props) {
  const { searchResult, handleShow } = props;
  const [sortField, setSortField] = useState('bookTitle');
  const [sortOrder, setSortOrder] = useState('asc');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!searchResult || searchResult.length === 0) return [];
    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

    return [...searchResult].sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';

      if (sortField === 'accessionNo') {
        const numA = parseInt(valA, 10);
        const numB = parseInt(valB, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortOrder === 'asc' ? numA - numB : numB - numA;
        }
      }

      const cmp = naturalCollator.compare(String(valA), String(valB));
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [searchResult, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));

  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(sortedData.length, startIndex + pageSize);
  const paginatedResults = sortedData.slice(startIndex, endIndex);

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1 small" style={{ opacity: 0.4 }}></i>;
    }
    return sortOrder === 'asc' ? (
      <i className="bi bi-arrow-up text-primary ms-1"></i>
    ) : (
      <i className="bi bi-arrow-down text-primary ms-1"></i>
    );
  };

  const renderStatusBadge = (status, scope) => {
    if (scope === 'Reference') {
      if (status === 'Issued') {
        return <Badge bg="warning" text="dark" className="px-2 py-1">Reference (Issued)</Badge>;
      }
      return <Badge bg="info" className="text-dark px-2 py-1">Reference</Badge>;
    }
    if (status === 'Active' || status === 'Available') {
      return <Badge bg="success" className="px-2 py-1">Available</Badge>;
    }
    if (status === 'Issued') {
      return <Badge bg="warning" text="dark" className="px-2 py-1">Issued</Badge>;
    }
    return <Badge bg="secondary" className="px-2 py-1">{status || 'Available'}</Badge>;
  };

  if (!searchResult || searchResult.length === 0) return null;

  return (
    <div className="card shadow-sm border-0 rounded-4 overflow-hidden bg-white mb-4">
      {/* Top Controls Toolbar */}
      <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-light border-bottom gap-2">
        <div className="d-flex align-items-center gap-2">
          <label className="small fw-semibold text-muted mb-0">Show</label>
          <select
            className="form-select form-select-sm"
            style={{ width: '80px' }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="small text-muted">books per page</span>
        </div>

        <div className="small text-muted">
          Showing <strong>{startIndex + 1}</strong> - <strong>{endIndex}</strong> of <strong>{sortedData.length}</strong> books
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table hover responsive className="mb-0 align-middle">
          <thead style={{ background: '#f1f5f9', color: '#063547', fontSize: '0.86rem', fontWeight: 700 }}>
            {props.isArticle || (sortedData.length > 0 && sortedData[0].articleID) ? (
              <tr>
                <th style={{ width: '45px' }} className="text-center">#</th>
                <th style={{ minWidth: '130px', cursor: 'pointer' }} onClick={() => handleSort('articleID')}>
                  Article ID {renderSortIcon('articleID')}
                </th>
                <th style={{ minWidth: '220px', cursor: 'pointer' }} onClick={() => handleSort('articleTitle')}>
                  Article Title {renderSortIcon('articleTitle')}
                </th>
                <th style={{ minWidth: '150px', cursor: 'pointer' }} onClick={() => handleSort('articleAuthor')}>
                  Author {renderSortIcon('articleAuthor')}
                </th>
                <th style={{ minWidth: '180px', cursor: 'pointer' }} onClick={() => handleSort('journalTitle')}>
                  Host Journal / Book {renderSortIcon('journalTitle')}
                </th>
                <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleSort('callNo')}>
                  Call No {renderSortIcon('callNo')}
                </th>
                <th style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleSort('startPageNo')}>
                  Pages {renderSortIcon('startPageNo')}
                </th>
                <th style={{ minWidth: '90px' }} className="text-center">Status</th>
              </tr>
            ) : (
              <tr>
                <th style={{ width: '50px' }} className="text-center">#</th>
                <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleSort('accessionNo')}>
                  Accession # {renderSortIcon('accessionNo')}
                </th>
                <th style={{ minWidth: '220px', cursor: 'pointer' }} onClick={() => handleSort('bookTitle')}>
                  Book Title {renderSortIcon('bookTitle')}
                </th>
                <th style={{ minWidth: '160px', cursor: 'pointer' }} onClick={() => handleSort('bookAuthor')}>
                  Author {renderSortIcon('bookAuthor')}
                </th>
                <th style={{ minWidth: '130px', cursor: 'pointer' }} onClick={() => handleSort('bookClassificationNo')}>
                  Call No / Shelf {renderSortIcon('bookClassificationNo')}
                </th>
                <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleSort('bookCategory')}>
                  Category {renderSortIcon('bookCategory')}
                </th>
                <th style={{ minWidth: '90px' }} className="text-center">Status</th>
              </tr>
            )}
          </thead>
          <tbody style={{ fontSize: '0.88rem' }}>
            {paginatedResults.map((item, index) => {
              if (props.isArticle || item.articleID) {
                const callNumber = item.callNo || item.journalCallNo || `${item.bookClassificationNo || ''} ${item.authorMark || ''}`.trim();
                const hostTitle = item.journalTitle || item.bookTitle || '';

                return (
                  <tr
                    key={item.articleID || item.accessionNo || startIndex + index}
                    onClick={(event) => handleShow(event, item)}
                    style={{ cursor: 'pointer' }}
                    className="vj-table-row"
                  >
                    <td className="text-center fw-bold text-muted">{startIndex + index + 1}</td>
                    <td>
                      <span className="badge bg-light text-primary border font-monospace px-2 py-1">
                        {item.articleID || `#${item.accessionNo}`}
                      </span>
                    </td>
                    <td>
                      <div className="fw-bold text-dark">{item.articleTitle}</div>
                      {item.articleSubTitle && item.articleSubTitle !== 'NA' && (
                        <div className="text-muted small">
                          <i>{item.articleSubTitle}</i>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="text-secondary">
                        <i className="bi bi-person me-1"></i>{item.articleAuthor || 'Unknown'}
                      </span>
                    </td>
                    <td>
                      <div className="small fw-semibold text-truncate" style={{ maxWidth: '200px' }} title={hostTitle}>
                        {hostTitle ? (
                          <>
                            <i className="bi bi-journal-album text-primary me-1"></i>
                            {hostTitle}
                          </>
                        ) : '—'}
                      </div>
                      <div className="d-flex align-items-center gap-1 mt-1">
                        {item.volissuesNo && (
                          <Badge bg="info" className="text-dark" style={{ fontSize: '0.7rem' }}>
                            Vol. {item.volissuesNo}
                          </Badge>
                        )}
                        {item.accessionNo && (
                          <span className="badge bg-light text-muted border font-monospace" style={{ fontSize: '0.7rem' }}>
                            Acc #{item.accessionNo}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-primary bg-opacity-10 text-primary font-monospace px-2 py-1">
                        {callNumber || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {item.startPageNo && item.endPageNo ? (
                        <span className="badge bg-light text-dark border">
                          pp. {item.startPageNo}–{item.endPageNo}
                        </span>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                        {item.articleStatus || 'Active'}
                      </span>
                    </td>
                  </tr>
                );
              }

              const callNumber = `${item.bookClassificationNo || ''} ${item.authorMark || ''}`.trim();

              const isBoundJournal = (item.bookAuthor || '').trim().toLowerCase() === 'bound journal';

              return (
                <tr
                  key={item.accessionNo || startIndex + index}
                  onClick={(event) => handleShow(event, item)}
                  style={{ cursor: 'pointer' }}
                  className="vj-table-row"
                >
                  <td className="text-center fw-bold text-muted">{startIndex + index + 1}</td>
                  <td>
                    <span className="badge bg-light text-primary border font-monospace px-2 py-1">
                      #{item.accessionNo}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap align-items-center gap-1">
                      <span className="fw-bold text-dark">{item.bookTitle}</span>
                      {isBoundJournal && (
                        <span className="badge-bound-journal">
                          <i className="bi bi-journal-bookmark-fill"></i> Bound Journal
                        </span>
                      )}
                    </div>
                    {item.bookSubTitle && item.bookSubTitle !== 'NA' && (
                      <div className="text-muted small">
                        <i>{item.bookSubTitle}</i>
                      </div>
                    )}
                  </td>
                  <td>
                    {isBoundJournal ? (
                      <span className="badge-bound-journal-subtle">
                        <i className="bi bi-journal-bookmark-fill"></i> Bound Journal
                      </span>
                    ) : (
                      <span className="text-secondary">
                        <i className="bi bi-person me-1"></i>{item.bookAuthor || 'Unknown'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-primary bg-opacity-10 text-primary font-monospace px-2 py-1">
                      {callNumber || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className="badge bg-light text-dark border">
                      {item.bookCategory || (isBoundJournal ? 'Periodical' : 'Theology')}
                    </span>
                  </td>
                  <td className="text-center">
                    {renderStatusBadge(item.bookStatus, item.bookScope)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-light border-top gap-2">
          <div className="small text-muted">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </div>

          <nav aria-label="Books pagination">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous"
                >
                  &laquo;
                </button>
              </li>
              {getPageNumbers().map((p) => (
                <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(p)}>
                    {p}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next"
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}
